import type {
  MRT_Column,
  MRT_ColumnDef,
  MRT_RowData,
  MRT_TableBodyCellProps,
  MRT_TableBodyRowProps,
  MRT_TableContainerProps,
  MRT_TableDetailPanelProps,
  MRT_TableHeadCellProps,
  MRT_TableInstance,
  MRT_TablePaperProps,
} from 'material-react-table';
import { reorderColumn } from 'material-react-table';
import type { RefObject, SyntheticEvent } from 'react';

const defaultTableBorderColor = 'rgba(255, 255, 255, 0.12)';

export type BaseTableRowData = MRT_RowData & {
  disabled?: boolean;
};

export type BaseTableStyleOverrides = {
  container?: Record<string, unknown>;
  header?: Record<string, unknown>;
  cell?: Record<string, unknown>;
  dropdown?: Record<string, unknown>;
  row?: Record<string, unknown>;
};

type BaseTableDragTable<TData extends MRT_RowData> = MRT_TableInstance<TData>;

const handleHeaderDragStart = <TData extends MRT_RowData>(
  event: React.DragEvent<HTMLTableCellElement>,
  column: MRT_Column<TData>,
  table: BaseTableDragTable<TData>,
  enableColumnOrdering: boolean,
): void => {
  if (!enableColumnOrdering || column.columnDef.enableColumnOrdering === false) return;
  if (
    (event.target as HTMLElement).closest('button, [role="button"], input, select, textarea, a')
  ) {
    return;
  }

  table.setDraggingColumn(column);

  try {
    event.dataTransfer.setDragImage(event.currentTarget, 0, 0);
  } catch {
    // Some browsers can reject custom drag images; reordering still works without it.
  }
};

const handleHeaderDragEnd = <TData extends MRT_RowData>(
  column: MRT_Column<TData>,
  table: BaseTableDragTable<TData>,
  enableColumnOrdering: boolean,
): void => {
  const { columnOrder = [], draggingColumn, hoveredColumn } = table.getState();
  const hoveredTableColumn = hoveredColumn?.id ? table.getColumn(hoveredColumn.id) : undefined;

  if (
    enableColumnOrdering &&
    hoveredTableColumn &&
    draggingColumn &&
    hoveredTableColumn.id !== draggingColumn.id
  ) {
    table.setColumnOrder(reorderColumn(column, hoveredTableColumn, columnOrder));
  }

  table.setDraggingColumn(null);
  table.setHoveredColumn(null);
};

const getHeaderLayoutStyles = (headerStyles: Record<string, unknown> = {}) => {
  const {
    background: _background,
    backgroundColor: _backgroundColor,
    bgcolor: _bgcolor,
    borderBottom: _borderBottom,
    borderRadius: _borderRadius,
    borderTopLeftRadius: _borderTopLeftRadius,
    borderTopRightRadius: _borderTopRightRadius,
    color: _color,
    fontSize: _fontSize,
    fontWeight: _fontWeight,
    ...layoutStyles
  } = headerStyles;

  return layoutStyles;
};

export const getBaseTablePaperProps = <TData extends MRT_RowData>(
  muiTablePaperProps: Partial<MRT_TablePaperProps<TData>>,
  customStyles: BaseTableStyleOverrides,
): Partial<MRT_TablePaperProps<TData>> => ({
  ...muiTablePaperProps,
  sx: {
    width: '100%',
    backgroundColor: 'var(--color-primary-shadow)',
    border: `1px solid ${defaultTableBorderColor}`,
    borderRadius: '0.375rem',
    color: 'var(--color-font)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    ...customStyles.container,
    ...(muiTablePaperProps.sx || {}),
  },
});

export const getBaseTableContainerProps = <TData extends MRT_RowData>(
  muiTableContainerProps: Partial<MRT_TableContainerProps<TData>>,
): Partial<MRT_TableContainerProps<TData>> => ({
  ...muiTableContainerProps,
  className: ['customScrollbar-Y', muiTableContainerProps.className].filter(Boolean).join(' '),
  sx: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    ...(muiTableContainerProps.sx || {}),
  },
});

export const baseTableDisplayColumnDefOptions: Record<
  string,
  Partial<MRT_ColumnDef<MRT_RowData>>
> = {
  'mrt-row-expand': {
    muiTableHeadCellProps: { sx: { display: 'none' } },
    muiTableBodyCellProps: { sx: { display: 'none' } },
  },
};

export const baseTableHeadProps = {
  sx: {
    backgroundColor: 'var(--color-primary-shadow)',
    opacity: 1,
    '& tr': {
      backgroundColor: 'var(--color-primary-shadow)',
    },
  },
};

export const baseTableProps = {
  sx: {
    backgroundColor: 'var(--color-base)',
    borderCollapse: 'separate',
    borderSpacing: 0,
    color: 'var(--color-font)',
  },
};

type BaseTableHeadCellContext<TData extends MRT_RowData> = {
  column: MRT_Column<TData>;
  table: MRT_TableInstance<TData>;
};

export const getBaseTableHeadCellProps = <TData extends MRT_RowData>(
  customStyles: BaseTableStyleOverrides,
  enableColumnOrdering: boolean,
) => {
  return ({
    column,
    table,
  }: BaseTableHeadCellContext<TData>): Partial<MRT_TableHeadCellProps<TData>> => {
    const headerLayoutStyles = getHeaderLayoutStyles(customStyles.header);
    const visibleColumns = table
      .getVisibleLeafColumns()
      .filter((visibleColumn) => visibleColumn.id !== 'mrt-row-expand');
    const isFirstVisibleColumn = visibleColumns[0]?.id === column.id;
    const isLastVisibleColumn = visibleColumns[visibleColumns.length - 1]?.id === column.id;

    return {
      draggable:
        enableColumnOrdering &&
        column.columnDef.columnDefType !== 'group' &&
        column.columnDef.enableColumnOrdering !== false,
      onDragStart: (event) => handleHeaderDragStart(event, column, table, enableColumnOrdering),
      onDragEnd: () => handleHeaderDragEnd(column, table, enableColumnOrdering),
      sx: {
        cursor:
          enableColumnOrdering && column.columnDef.enableColumnOrdering !== false
            ? 'grab'
            : undefined,
        '&:active': {
          cursor:
            enableColumnOrdering && column.columnDef.enableColumnOrdering !== false
              ? 'grabbing'
              : undefined,
        },
        backgroundColor: 'var(--color-primary-shadow)',
        borderBottom: `1px solid ${defaultTableBorderColor}`,
        color: 'var(--color-font)',
        fontSize: '0.875rem',
        fontWeight: 600,
        '& .Mui-TableHeadCell-Content-Wrapper': { color: 'var(--color-font)' },
        '& .MuiButtonBase-root': { color: 'var(--color-font)', opacity: 1 },
        '& .MuiButtonBase-root:hover': { color: 'var(--color-font)' },
        '& .MuiTableSortLabel-root': { color: 'inherit !important', marginLeft: '0.25rem' },
        '&:not([data-sort]) .MuiTableSortLabel-root': {
          opacity: '0 !important',
          pointerEvents: 'none',
          width: 0,
        },
        '&[data-sort="asc"] .MuiTableSortLabel-root, &[data-sort="desc"] .MuiTableSortLabel-root': {
          opacity: '1 !important',
          width: '1.25rem !important',
        },
        '& .MuiTableSortLabel-icon': {
          color: 'var(--color-font) !important',
          fill: 'var(--color-font) !important',
          fontSize: '1.25rem',
          opacity: '1 !important',
        },
        '&[data-sort="asc"] .MuiTableSortLabel-icon': { transform: 'rotate(180deg)' },
        '&[data-sort="desc"] .MuiTableSortLabel-icon': { transform: 'rotate(0deg)' },
        boxShadow: `inset 0 1px 0 ${defaultTableBorderColor}`,
        padding: '8px',
        ...headerLayoutStyles,
        ...(isFirstVisibleColumn && { borderTopLeftRadius: '0.375rem' }),
        ...(isLastVisibleColumn && { borderTopRightRadius: '0.375rem' }),
      },
    };
  };
};

export const getBaseTableBodyCellProps =
  <TData extends MRT_RowData>(customStyles: BaseTableStyleOverrides) =>
  (): Partial<MRT_TableBodyCellProps<TData>> => ({
    sx: {
      backgroundColor: 'inherit',
      color: 'var(--color-font)',
      ...customStyles.cell,
      borderBottom: `1px solid ${defaultTableBorderColor}`,
      '&:last-of-type': { borderRight: 'none' },
    },
  });

export const getBaseTableDetailPanelProps =
  <TData extends MRT_RowData>(customStyles: BaseTableStyleOverrides) =>
  (): Partial<MRT_TableDetailPanelProps<TData>> => ({
    className: 'fusion-base-table-detail-panel',
    sx: {
      backgroundColor: 'var(--color-primary-highlight)',
      color: 'var(--color-primary-highlight-foreground)',
      ...customStyles.dropdown,
    },
  });

export type BaseTablePinHoldState = {
  didPin: boolean;
  timer: ReturnType<typeof setTimeout> | null;
};

type BaseTableBodyRowOptions<TData extends BaseTableRowData> = {
  altRowSettings: { alternate?: boolean; color1?: string; color2?: string };
  customStyles: BaseTableStyleOverrides;
  expandable: boolean;
  onRowClick?: (row: TData) => void;
  preserveSelectedRowOnHover: boolean;
  rowPinHoldRef?: RefObject<BaseTablePinHoldState | null>;
  rowPinningHoldDelay: number;
  selectedRowId: unknown;
  selectedRowIds: unknown[];
  togglePinnedRow?: (rowId: string) => void;
};

type BaseTableBodyRowContext<TData extends BaseTableRowData> = Pick<
  MRT_TableBodyRowProps<TData>,
  'row'
>;

export const getBaseTableBodyRowProps =
  <TData extends BaseTableRowData>({
    altRowSettings,
    customStyles,
    expandable,
    onRowClick,
    preserveSelectedRowOnHover,
    rowPinHoldRef,
    rowPinningHoldDelay,
    selectedRowId,
    selectedRowIds,
    togglePinnedRow,
  }: BaseTableBodyRowOptions<TData>) =>
  ({ row }: BaseTableBodyRowContext<TData>): Partial<MRT_TableBodyRowProps<TData>> => {
    const disabled = !!row.original.disabled;
    const rowId = row.original.id;
    const isSelectedById =
      selectedRowId != null && `${rowId ?? ''}` !== '' && `${rowId}` === `${selectedRowId}`;
    const isSelectedByIds = selectedRowIds.some((selectedId) => `${selectedId}` === `${rowId}`);
    const isSelected = isSelectedById || isSelectedByIds;
    const canPinRow = !!togglePinnedRow && !disabled;

    const clearPinHold = (): void => {
      if (rowPinHoldRef?.current?.timer) clearTimeout(rowPinHoldRef.current.timer);
      if (rowPinHoldRef?.current) rowPinHoldRef.current.timer = null;
    };

    const startPinHold = (event: SyntheticEvent<HTMLTableRowElement>): void => {
      const isInteractiveTarget =
        event.target instanceof HTMLElement
          ? event.target.closest('button, [role="button"], input, select, textarea, a')
          : null;

      if (!canPinRow || isInteractiveTarget) return;

      clearPinHold();
      if (rowPinHoldRef?.current) {
        rowPinHoldRef.current.didPin = false;
        rowPinHoldRef.current.timer = setTimeout(() => {
          rowPinHoldRef.current!.didPin = true;
          togglePinnedRow!(row.id);
        }, rowPinningHoldDelay);
      }
    };

    return {
      'aria-selected': isSelected || undefined,
      onClick: () => {
        if (disabled) return;
        if (rowPinHoldRef?.current?.didPin) {
          rowPinHoldRef.current.didPin = false;
          return;
        }
        if (expandable) row.toggleExpanded();
        onRowClick?.(row.original);
      },
      onMouseDown: startPinHold,
      onMouseLeave: clearPinHold,
      onMouseUp: clearPinHold,
      onTouchCancel: clearPinHold,
      onTouchEnd: clearPinHold,
      onTouchStart: startPinHold,
      hover: false,
      sx: {
        backgroundColor: 'var(--color-base)',
        color: 'var(--color-font)',
        ...(altRowSettings?.alternate && {
          backgroundColor: row.index % 2 ? altRowSettings.color1 : altRowSettings.color2,
        }),
        '& td': { color: 'var(--color-font)' },
        ...customStyles.row,
        ...(isSelected && {
          backgroundColor: 'var(--color-primary-highlight)',
          color: 'var(--color-primary-highlight-foreground)',
          '& td': { color: 'var(--color-primary-highlight-foreground)' },
          ...(preserveSelectedRowOnHover && {
            '&:hover': { backgroundColor: 'var(--color-primary-highlight)' },
          }),
        }),
        ...(disabled && { cursor: 'not-allowed' }),
        ...(!disabled && onRowClick && { cursor: 'pointer' }),
        ...(canPinRow && {
          '&:active': { outline: '1px solid var(--color-primary-highlight)' },
        }),
        ...(row.getIsPinned?.() && {
          boxShadow: 'inset 3px 0 0 var(--color-primary-highlight)',
          '& .MuiIconButton-root': { color: 'var(--color-font)' },
        }),
      },
    };
  };
